import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { FlaskConical, AlertTriangle, CheckCircle, XCircle, ShieldCheck, Scale, Clock, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const DisclaimerModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const hasAgreed = localStorage.getItem('pept-disclaimer-agreed');
    if (!hasAgreed) {
      setIsOpen(true);
    }
  }, []);

  const handleAgree = () => {
    localStorage.setItem('pept-disclaimer-agreed', 'true');
    setIsOpen(false);
  };

  const handleDisagree = () => {
    window.location.href = 'https://google.com';
  };

  const disclaimerItems = [
    {
      icon: Clock,
      title: t('disclaimer.ageRequirement'),
      text: t('disclaimer.ageText'),
    },
    {
      icon: FlaskConical,
      title: t('disclaimer.productUse'),
      text: t('disclaimer.productUseText'),
    },
    {
      icon: XCircle,
      title: t('disclaimer.noMedicalAdvice'),
      text: t('disclaimer.noMedicalAdviceText'),
    },
    {
      icon: Scale,
      title: t('disclaimer.compliance'),
      text: t('disclaimer.complianceText'),
    },
    {
      icon: Package,
      title: t('disclaimer.salesFinal'),
      text: t('disclaimer.salesFinalText'),
    },
    {
      icon: ShieldCheck,
      title: t('disclaimer.exportOnly'),
      text: t('disclaimer.exportOnlyText'),
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-white"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="disclaimer-modal"
      >
        <DialogHeader className="text-center pb-4 border-b">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-slate-100 rounded-sm">
              <FlaskConical className="w-10 h-10 text-slate-900" strokeWidth={1.5} />
            </div>
          </div>
          <DialogTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            PEP-T
          </DialogTitle>
          <DialogDescription className="text-base text-slate-600 mt-2">
            {t('disclaimer.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {disclaimerItems.map((item, index) => (
            <div
              key={index}
              className="flex gap-4 p-4 bg-slate-50 border-l-2 border-slate-900 rounded-r-sm"
              data-testid={`disclaimer-item-${index}`}
            >
              <item.icon className="w-5 h-5 text-slate-700 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-slate-900 text-sm">{item.title}</h4>
                <p className="text-sm text-slate-600 mt-1">{item.text}</p>
              </div>
            </div>
          ))}

          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-sm">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              {t('disclaimer.confirmText')}
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleDisagree}
            className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-100 rounded-sm"
            data-testid="disclaimer-disagree-btn"
          >
            <XCircle className="w-4 h-4 mr-2" />
            {t('disclaimer.disagree')}
          </Button>
          <Button
            onClick={handleAgree}
            className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
            data-testid="disclaimer-agree-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {t('disclaimer.agree')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
